import { Component, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { State, process } from '@progress/kendo-data-query';
import { FlowManagementService } from '../services/flow-management.service';
import { Flow } from '../obj/Flow';
import { PaymentService } from '../services/payment.service';
import { FlowEvent, FlowEventService } from '../services/flow-event.service';
import { WindowRef, DialogService, DialogRef } from '@progress/kendo-angular-dialog';
import { BehaviorSubject, Observable, Subscription, from } from 'rxjs';
import { ApplicationInsightsService } from '../services/application-insights.service';
import { StorageRouterService } from '../services/storage-router.service';

@Component({
    selector: 'app-flow-versions',
    templateUrl: './flow-versions.component.html',
    styleUrls: ['./flow-versions.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class FlowVersionsComponent implements OnInit, OnChanges, OnDestroy {

  private subscription = new Subscription();

  state: State = {
    skip: 0,
    take: null,

    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };
  view: GridDataResult;
  // state$: BehaviorSubject<State> = new BehaviorSubject(this.state);
  // view$: Observable<GridDataResult>;

  /**
   * The flow to load snapshots for. Set imperatively when launched in a Kendo
   * Window dialog, or bound declaratively when embedded as a tab — both paths
   * trigger the same `refreshData()` cycle.
   */
  @Input() public flow: Flow = null;
  public versions: any[] = [];
  ready = false;
  error = null;

  public selection: string[] = [];
  membershipStatus = '';

  @ViewChild('rungrid', {static: true}) rungrid;

  constructor(
    private flowManagementService: FlowManagementService,
    // private windowService: WindowService,
    // private analytics: AnalyticsService,
    private dialogService: DialogService,
    private flowEventService: FlowEventService,
    private paymentService: PaymentService,
    /**
     * Optional — present when the component is hosted in a Kendo Window;
     * absent when embedded as a tab. `doDiff` only closes the window when it
     * exists, so embedded usage doesn't require a host window.
     */
    @Optional() private windowRef: WindowRef | null,
    private appInsights: ApplicationInsightsService,
    private storageService: StorageRouterService,
    private router: Router,
  ) { }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    //this.view = process(this.versions, this.state);
    this.refreshGrid();
  }

  async refreshData() {
    if (!this.flow) return;

    // Paint immediately. 'current' is always present; any runs already on the
    // flow object (from a prior /runs visit, or hydrated from the OPFS cache
    // via wireCachedRuns below) show up as historical versions right away.
    // The page no longer goes blank waiting for the network call.
    this.applyVersionsFromFlow();
    this.ready = true;

    // Stream cached runs from storage so deep-linking to /snapshots paints
    // the version list before the fresh fetch lands. Same pattern flow-runs
    // uses; subscription cleaned up on destroy.
    this.wireCachedRuns(this.flow.environment, this.flow.flowName);

    var status = await this.paymentService.checkSubscription();
    if (!status) {
      this.membershipStatus = '';
      return;
    }

    // Fire a fresh runs fetch in the background; on resolve, re-render the
    // versions list with whatever the network produced.
    try {
      await this.flowManagementService.getFlowRuns(this.flow, false, false, false, null, true);
      this.applyVersionsFromFlow();
    } catch (error) {
      this.error = error;
    }
  }

  /** Build the versions list from the current `this.flow` state — 'current'
   *  always first, then one entry per cached/fetched run. */
  private applyVersionsFromFlow(): void {
    if (!this.flow) return;
    const runs = this.flow.runs || [];
    const versions = runs.map(run => ({
      'Name': run.name,
      'VersionNumber': run.name,
      'Modified': run.startTime,
    }));
    this.versions = [
      {
        'Name': 'current',
        'VersionNumber': 'current',
        'Modified': this.flow.modified,
      },
      ...versions,
    ];
    this.refreshGrid();
  }

  /** Subscribe to the storage runs stream filtered to this flow so cached
   *  runs paint into the versions list the moment the page mounts. Stays
   *  subscribed for the component's lifetime; later cache writes (the fresh
   *  fetch landing) re-render in place. */
  private wireCachedRuns(envName: string, flowName: string): void {
    if (!envName || !flowName) return;
    this.subscription.add(
      this.storageService.getRuns$(envName).subscribe((cached) => {
        const flowRuns = (cached || []).filter((r) => r.flowName === flowName);
        if (flowRuns.length === 0) return;
        // Mutate in place so applyVersionsFromFlow() sees them. Sorted by
        // start time desc to match the network result's order.
        this.flow.runs = flowRuns.slice().sort((a, b) => {
          const at = a.startTime ? a.startTime.getTime() : 0;
          const bt = b.startTime ? b.startTime.getTime() : 0;
          return bt - at;
        });
        this.applyVersionsFromFlow();
      })
    );
  }

  refreshGrid() {
    //this.runs = this.flow.runsLoaded;
    this.state.take = this.versions.length;
    this.view = process(this.versions, this.state);
  }

  ngOnInit() {

    this.membershipStatus = this.paymentService.membership.status;
    this.paymentService.membership$.subscribe((membership) => {
      this.membershipStatus = membership.status;
    });

    if (this.flow) {
      this.refreshData();
    }
  }

  /**
   * Re-run the data load when the bound `flow` changes (e.g. a parent tab
   * switches the active flow). Skipped on the first set so it doesn't
   * double-fire alongside ngOnInit.
   */
  ngOnChanges(changes: SimpleChanges) {
    const change = changes['flow'];
    if (change && !change.firstChange && change.currentValue) {
      this.ready = false;
      this.versions = [];
      // Drop the previous flow's cached-runs subscription before wiring a new
      // one in refreshData, so we don't double-listen across flow changes.
      this.subscription.unsubscribe();
      this.subscription = new Subscription();
      this.refreshData();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }



  public onSelect({ dataItem, item }): void {

    this.appInsights.trackEvent(`versions`, { "select": item });

    switch(item) {
      case "Restore":
        this.doRestore(dataItem);
        break;
      case "Diff":
        this.doDiff();
        break;

      case "Save Copy":
        this.doRestore(dataItem, true);
        break;
    }
  }

  async doRestore(item, clone = false) {
    var status = await this.paymentService.checkSubscription();
    if (!status) return;

    this.ready = false;

    try {

      if (this.selection.length !== 1) {

        this.dialogService.open({
          title: 'Need 1 selection',
          content: `Snapshots restore needs one selected version.`,
          actions: [
              { text: 'OK', primary: true }
          ],
          width: 450,
          height: 200,
          minWidth: 250
        });
        return;
      }
        
      const content = clone ?
        `Snapshots clone will save the selected version into a new flow.` :
        `Snapshots restore will restore the selected version over the current flow.`;

      const dialog: DialogRef = this.dialogService.open({
        title: 'Snapshots Restore',
        content: content,
        actions: [
            { text: 'Yes', primary: true },
            { text: 'No' }
        ],
        width: 450,
        height: 200,
        minWidth: 250
      });
      var result:any = await dialog.result.toPromise();

      if (result.text != 'Yes') {
        this.ready = true;
        return;
      }

      const name = item ? item.name : this.selection[0];

      //var definition = await this.flowManagementService.getHistory(this.flow, item.Name);
      const left = await this.flowManagementService.getFlowFromRun(`${this.flow.environment}/flows/${this.flow.flowName}/runs/${name}`).toPromise();
      const definition = left.properties.flow.properties.definition;

      var flow = await this.flowManagementService.getFlow(this.flow.environment, this.flow.flowName).toPromise();

      if (clone) {

        var flowData = {
            "properties": {
                "definition": definition,
                "connectionReferences": flow.properties.connectionReferences,
                "displayName": flow.properties.displayName + " Copy",
                "templateName": flow.properties.templateName,
                "environment": {
                    "name": flow.environment
                },
                "state": "Stopped",
            }
        };
        await this.flowManagementService.createFlow(this.flow.environment, flowData).toPromise();

      }
      else {
        flow.properties.definition = definition;
        await this.flowManagementService.updateFlow(this.flow.environment, this.flow.flowName, flow).toPromise();

      }

      // await this.flowManagementService.getFlowHistory(this.flow).then(versions=>{

      //   this.versions = versions;
      //   this.refreshGrid();

      // });

      await this.refreshData();
    }
    catch(error) {
      this.error = error;
    }
    finally {
      this.ready = true;
    }

  }

  async doDiff() {
    var status = await this.paymentService.checkSubscription();
    if (!status) return;

    if (this.selection.length != 2) {

      this.dialogService.open({
        title: 'Need 2 selections',
        content: `Snapshots diff needs two selected versions.`,
        actions: [
            { text: 'OK', primary: true }
        ],
        width: 450,
        height: 200,
        minWidth: 250
    });


      return;
    }

    // Navigate to the diff route. Both sides are the same flow so envName
    // and flowName repeat — only the version segments change.
    const [leftVersion, rightVersion] = this.selection.map(name => name || 'current');
    this.router.navigate([
      '/flows',
      this.flow.environment,
      this.flow.flowName,
      leftVersion,
      'diff',
      this.flow.environment,
      this.flow.flowName,
      rightVersion,
    ]);

    // Close the host window when we're inside one (dialog-launched). Embedded
    // tab/route usage has no WindowRef so this is a no-op there.
    this.windowRef?.close();
  }

}
